import   React            ,
       { useEffect        ,
         useRef           ,
         useState         }  from 'react';

import   classNames          from 'classnames';

import   styles              from './AdminResourcesTab.module.scss';
import   Icon                from '../../widget/images/Icon';
import   CreatorVideosPanel   from '../dashboardV2Managment/CreatorVideosPanel';
import   CreatorChannelsPanel from '../dashboardV2Managment/CreatorChannelsPanel';
import   CreatorRecordingsPanel from '../dashboardV2Managment/CreatorRecordingsPanel';
import   CreatorProductsPanel from '../dashboardV2Managment/CreatorProductsPanel';
import   CreatorSocialPanel   from '../dashboardV2Managment/CreatorSocialPanel';
import   CreatorRegiePanel    from '../dashboardV2Managment/CreatorRegiePanel';
import   CreatorDocumentsPanel from '../dashboardV2Managment/CreatorDocumentsPanel';
import   CreatorAvatarModelsPanel from '../dashboardV2Managment/CreatorAvatarModelsPanel';
import   CreatorMediaPanel    , { MediaKind } from '../dashboardV2Managment/CreatorMediaPanel';

import { WWW_URLS          }  from '../../../backendServices/urls';
import { getAdminResources ,
         getAdminResourceItems ,
         AdminResources    ,
         AdminResourceQuota ,
         AdminResourceItems }  from '../../../backendServices/adminManagment/api';

// Onglet « Ressources » de la fiche créateur : tout ce qu'il possède, d'un coup d'œil —
// quotas consommés (appareils, chaînes, disque, durées) puis fichiers et objets rattachés
// (chaînes, médias par type, enregistrements, produits, régie, réseaux sociaux).
// Lecture seule : les plafonds et les remises à zéro se règlent dans « Tableau de bord ».

// Convention binaire du projet (1 Go = 1024³ octets), comme les quotas.
const formatSize = (bytes: number): string => {
  if (!isFinite(bytes) || bytes <= 0) return '0 o';
  const units = ['o', 'Ko', 'Mo', 'Go', 'To'];
  let v = bytes, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toLocaleString('fr-FR', { maximumFractionDigits: i < 2 ? 0 : 1 })} ${units[i]}`;
};

const formatDuration = (seconds: number): string => {
  const t = Math.max(0, Math.round(seconds)), h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60);
  return `${h} h ${String(m).padStart(2, '0')} min`;
};

const MEDIA_LABELS: Record<string, string> = {
  Video         : 'Vidéos'            ,
  Image         : 'Images'            ,
  Sound         : 'Sons'              ,
  Document      : 'Documents'         ,
  AudioSlideshow: 'Diaporamas audio'  ,
};

// Grosse icône affichée à la place de la vignette quand une ressource n'a pas d'image.
const KIND_ICONS: Record<string, string> = {
  recordings     : '🎬',
  Sound          : '🎵',
  Document       : '📄',
  AudioSlideshow : '🎞️',
  Video          : '🎥',
  Image          : '🖼️',
  channels       : '📺',
  products       : '🛍️',
  regie          : '🎛️',
  social         : '🔗',
};

// Pictogramme « download » du jeu d'icônes du projet (même famille que « upload » dans FieldMedia).
const DownloadIcon = () => <Icon name="download" color="#fff" hoverColor="#fff" width={16} height={16} />;

interface QuotaCardProps {
  label  : string;
  quota  : AdminResourceQuota;
  format : (n: number) => string;
}

// Un max à 0 veut dire « aucun plafond posé » dans les fiches : on n'affiche pas de barre.
const QuotaCard = ({ label , quota , format }: QuotaCardProps) => {
  const ratio = quota.max > 0 ? Math.min(100, (quota.used / quota.max) * 100) : 0;

  return (
    <div className={classNames(styles.card)}>
      <span className={classNames(styles.cardLabel)}>{label}</span>
      <span className={classNames(styles.cardValue)}>
        {format(quota.used)}{quota.max > 0 ? ` / ${format(quota.max)}` : ''}
      </span>
      {
       quota.max > 0 &&
       <div className={classNames(styles.bar)}>
         <div className={classNames(styles.barFill, ratio >= 90 && styles.barFillHigh)} style={{ width: `${ratio}%` }} />
       </div>
      }
    </div>
  );
};

// Statuts de chaîne (channelStatus) → libellé ; « active » et « restricted » sont les deux états où la
// chaîne tourne, et sont mis en évidence dans les cartes (vert / violet, comme les pastilles de la liste).
const CHANNEL_STATUS_LABELS: Record<string, string> = {
  active                  : 'Active'                   ,
  restricted              : 'Active en privé'          ,
  activating              : 'Activation…'              ,
  deactivating            : 'Désactivation…'           ,
  activating_restricted   : 'Activation (privé)…'      ,
  deactivating_restricted : 'Désactivation (privé)…'   ,
  inactive                : 'Inactive'                 ,
  error                   : 'Erreur'                   ,
};

const formatDate = (iso: string | null): string =>
  iso ? new Date ( iso ).toLocaleString ( 'fr-FR' , { dateStyle: 'short' , timeStyle: 'short' } ) : '';

interface ResourceRowProps {
  adminId : number;
  email   : string;   // vidéos : le panneau du Dashboard filtre par courriel
  kind    : string;   // clé du détail côté serveur (channels, Video, recordings, …)
  label   : string;
  count   : React.ReactNode;
  detail  : string;
  expandable : boolean;
  // Case à cocher posée à côté du libellé (ex. « Montrer les chaînes supprimées »). Quand
  // includeDeleted change, le détail déjà chargé est rechargé.
  extra?          : React.ReactNode;
  includeDeleted? : boolean;
}

// Ligne du tableau, dépliable : le détail (les 60 plus récents) n'est demandé qu'au premier clic,
// puis gardé tant que l'onglet reste ouvert. Les cartes reprennent celles du dashboard.
const ResourceRow = ({ adminId , email , kind , label , count , detail , expandable , extra , includeDeleted = false }: ResourceRowProps) => {

  // Vidéos, chaînes, enregistrements, produits, réseaux sociaux, régie, documents, images, sons et diaporamas : le panneau du Dashboard (filtres, cartes, liste) charge ses propres données.
  const dashboardPanel = kind === 'Video' || kind === 'channels' || kind === 'recordings' || kind === 'products' || kind === 'social' || kind === 'regie' || kind === 'Document' || kind === 'Image' || kind === 'Sound' || kind === 'AudioSlideshow' || kind === 'avatarModels';
  const [ open    , setOpen    ] = useState(false);
  const [ loading , setLoading ] = useState(false);
  const [ items   , setItems   ] = useState<AdminResourceItems | null>(null);
  const [ failed  , setFailed  ] = useState(false);

  const load = async () => {

    setLoading ( true );
    setFailed  ( false );

    const result = await getAdminResourceItems ( adminId , kind , includeDeleted );

    if ( result.code === 1 ) setItems ( result.payload );
    else                     setFailed ( true );

    setLoading ( false );
  };

  // La case change : le détail affiché n'est plus le bon. On le recharge s'il est ouvert, sinon
  // on l'oublie pour que la prochaine ouverture le redemande.
  const firstRender = useRef ( true );

  useEffect(() => {

    if ( firstRender.current ) { firstRender.current = false; return; }

    setItems ( null );

    if ( open && !dashboardPanel ) load();

  }, [ includeDeleted ]);

  const toggle = async () => {

    if ( ! expandable ) return;

    const next = ! open;
    setOpen ( next );

    if ( next && !dashboardPanel && items === null && ! loading ) load();
  };

  return (
    <>
      <tr className={classNames(expandable && styles.rowClickable)} onClick={toggle}>
        <td><span className={classNames(styles.chevron, open && styles.chevronOpen)}>{expandable ? '▸' : ''}</span>{label}{extra}</td>
        <td>{count}</td>
        <td>{detail}</td>
      </tr>
      {
       open &&
       <tr>
         <td colSpan={3} className={classNames(styles.detailCell)}>
           {kind === 'Video' && <CreatorVideosPanel adminId={adminId} />}
           {kind === 'channels' && <CreatorChannelsPanel adminId={adminId} includeDeleted={includeDeleted} />}
           {kind === 'recordings' && <CreatorRecordingsPanel adminId={adminId} />}
           {kind === 'products' && <CreatorProductsPanel adminId={adminId} />}
           {kind === 'social' && <CreatorSocialPanel adminId={adminId} />}
           {kind === 'regie' && <CreatorRegiePanel adminId={adminId} />}
           {kind === 'Document' && <CreatorDocumentsPanel adminId={adminId} />}
           {kind === 'avatarModels' && <CreatorAvatarModelsPanel adminId={adminId} />}
           {( kind === 'Image' || kind === 'Sound' || kind === 'AudioSlideshow' ) && <CreatorMediaPanel adminId={adminId} kind={kind as MediaKind} />}
           {!dashboardPanel && loading && <span className={classNames(styles.muted)}>Chargement…</span>}
           {!dashboardPanel && failed  && <span className={classNames(styles.muted)}>Impossible de charger le détail.</span>}
           {
            !dashboardPanel && items &&
            <>
              <div className={classNames(styles.tiles)}>
                {
                 // Chaînes : les actives, puis les publiques, en tête (le tri est stable : l'ordre
                 // du serveur, du plus récent au plus ancien, est gardé à égalité).
                 [ ...items.items ].sort ( (x, y) => kind !== 'channels' ? 0 :
                   ( ( y.badge === 'active' || y.badge === 'restricted' ? 2 : 0 ) + ( y.public ? 1 : 0 ) ) -
                   ( ( x.badge === 'active' || x.badge === 'restricted' ? 2 : 0 ) + ( x.public ? 1 : 0 ) ) ).map ( (it) => {
                   const image = it.publicRef && it.image
                                 ? `${WWW_URLS.MediaServer}${it.publicRef}/images/${it.image === 'logo' ? 'logo' : 'thumbnail'}.webp`
                                 : '';

                   // Chaînes : « active » = verte, « active en privé » = violette, « publique » = bleue.
                   const isChannel = kind === 'channels';
                   const isActive  = isChannel && it.badge === 'active';
                   const isPrivate = isChannel && it.badge === 'restricted';
                   const isPublic  = isChannel && it.public === true;

                   return (
                     <div key={it.id} className={classNames(styles.tile, isActive && styles.tileActive, isPrivate && styles.tilePrivate, isChannel && it.badge === 'Supprimée' && styles.tileDeleted)}>
                       <div className={classNames(styles.photoWrap)}>
                         {image ? <img className={classNames(styles.photo)} src={image} alt={it.title} loading="lazy" />
                                : <div className={classNames(styles.photo, styles.photoIcon)} aria-hidden="true">{KIND_ICONS[kind] ?? ''}</div>}
                         {
                          ( kind === 'recordings' || kind === 'regie' ) &&
                          ( it.downloadUrl
                            ? <a className={classNames(styles.download)} href={it.downloadUrl} download target="_blank" rel="noreferrer"
                                 title={kind === 'regie' ? "Télécharger le dernier enregistrement de cette chaîne" : "Télécharger l'enregistrement"}
                                 onClick={(e) => e.stopPropagation()}><DownloadIcon /></a>
                            : <span className={classNames(styles.download, styles.downloadOff)}
                                    title={kind === 'regie' ? "Aucun enregistrement téléchargeable pour cette chaîne" : "Téléchargement indisponible : le fichier n'est pas encore sur le serveur de médias"}><DownloadIcon /></span> )
                         }
                       </div>
                       <div className={classNames(styles.tileBody)}>
                         <span className={classNames(styles.tileTitle)} title={it.title}>{it.title || '—'}</span>
                         {it.subtitle && <span className={classNames(styles.sub)} title={it.subtitle}>{it.subtitle}</span>}
                         {it.date && <span className={classNames(styles.sub)}>{formatDate(it.date)}</span>}
                         {
                          isChannel
                          ? <span className={classNames(styles.chips)}>
                              {it.badge === 'Supprimée'
                               ? <span className={classNames(styles.chip, styles.chipDeleted)}>Supprimée</span>
                               : <span className={classNames(styles.chip, isActive ? styles.chipActive : isPrivate ? styles.chipPrivate : styles.chipOff)}>
                                   {CHANNEL_STATUS_LABELS[it.badge ?? ''] ?? it.badge}
                                 </span>}
                              {isPublic && <span className={classNames(styles.chip, styles.chipPublic)}>Publique</span>}
                            </span>
                          : ( it.badge || ( it.bytes !== null && it.bytes > 0 ) ) &&
                            <span className={classNames(styles.sub)}>{[ it.badge , it.bytes ? formatSize(it.bytes) : '' ].filter(Boolean).join(' · ')}</span>
                         }
                       </div>
                     </div>
                   );
                 } )
                }
              </div>
              {
               items.total > items.items.length &&
               <span className={classNames(styles.muted)}>Les {items.items.length} plus récents sur {items.total}.</span>
              }
            </>
           }
         </td>
       </tr>
      }
    </>
  );
};

export interface AdminResourcesTabProps {
  adminId : number;
  email   : string;
}

export const AdminResourcesTab = ({ adminId , email }: AdminResourcesTabProps) => {

  const [ resources , setResources ] = useState<AdminResources | null>(null);
  const [ error     , setError     ] = useState<string | null>(null);

  // Les chaînes supprimées sont masquées par défaut.
  const [ showDeletedChannels , setShowDeletedChannels ] = useState(false);

  useEffect(() => {

    let cancelled = false;

    setResources ( null );
    setError     ( null );

    getAdminResources ( adminId ).then ( (result) => {
      if ( cancelled ) return;
      if ( result.code === 1 ) setResources ( result.payload );
      else                     setError ( "Impossible de charger les ressources de ce créateur." );
    } );

    return () => { cancelled = true; };

  }, [ adminId ]);

  if ( error     ) return <div className={classNames(styles.root)}>{error}</div>;
  if ( !resources ) return <div className={classNames(styles.root)}>Chargement des ressources…</div>;

  const unavailable = <span className={classNames(styles.muted)}>indisponible</span>;
  const q           = resources.quotas;

  return (
    <div className={classNames(styles.root)}>

      <h3 className={classNames(styles.title)}>Quotas consommés</h3>

      <div className={classNames(styles.cards)}>
        <QuotaCard label="Appareils"              quota={q.devices}   format={(n) => `${n}`} />
        <QuotaCard label="Chaînes"                quota={q.channels}  format={(n) => `${n}`} />
        <QuotaCard label="Espace disque"          quota={q.disk}      format={formatSize} />
        <QuotaCard label="Durée de diffusion"     quota={q.broadcast} format={formatDuration} />
        <QuotaCard label="Durée d'enregistrement" quota={q.recording} format={formatDuration} />
      </div>

      <h3 className={classNames(styles.title)}>Ce qu'il possède <span className={classNames(styles.muted)}>(cliquez sur une ligne pour la déplier)</span></h3>

      <table className={classNames(styles.table)}>
        <thead>
          <tr><th>Ressource</th><th>Quantité</th><th>Détail</th></tr>
        </thead>
        <tbody>
          <ResourceRow adminId={adminId} email={email} kind="channels" label="Chaînes"
                       count={resources.channels ? resources.channels.total + ( showDeletedChannels ? resources.channels.deleted : 0 ) : unavailable}
                       detail={resources.channels ? `${resources.channels.active} active(s)${showDeletedChannels && resources.channels.deleted > 0 ? ` · dont ${resources.channels.deleted} supprimée(s)` : ''}` : ''}
                       expandable={!!resources.channels && resources.channels.total + ( showDeletedChannels ? resources.channels.deleted : 0 ) > 0}
                       includeDeleted={showDeletedChannels}
                       extra={
                         <label className={classNames(styles.inlineCheck)} onClick={(e) => e.stopPropagation()}>
                           <input type="checkbox" checked={showDeletedChannels} onChange={(e) => setShowDeletedChannels(e.target.checked)} />
                           Montrer les chaînes supprimées
                         </label>
                       } />
          {
           resources.media === null
           ? <tr><td>Médias</td><td>{unavailable}</td><td /></tr>
           : resources.media.length === 0
             ? <tr><td>Médias</td><td>0</td><td /></tr>
             : resources.media.map ( (m) => (
                 <ResourceRow key={m.type} adminId={adminId} email={email} kind={m.type} label={MEDIA_LABELS[m.type] ?? m.type}
                              count={m.count} detail={formatSize(m.bytes)} expandable={m.count > 0} />
               ) )
          }
          <ResourceRow adminId={adminId} email={email} kind="recordings" label="Enregistrements"
                       count={resources.recordings ? resources.recordings.count : unavailable}
                       detail={resources.recordings ? `${formatSize(resources.recordings.bytes)} · ${formatDuration(resources.recordings.seconds)}` : ''}
                       expandable={!!resources.recordings && resources.recordings.count > 0} />
          <ResourceRow adminId={adminId} email={email} kind="products" label="Produits"
                       count={resources.products ?? unavailable} detail=""
                       expandable={!!resources.products && resources.products > 0} />
          <ResourceRow adminId={adminId} email={email} kind="regie" label="Configurations de régie"
                       count={resources.regieConfigs ?? unavailable} detail=""
                       expandable={!!resources.regieConfigs && resources.regieConfigs > 0} />
          <ResourceRow adminId={adminId} email={email} kind="avatarModels" label="Modèles 3D"
                       count={resources.avatarModels ? resources.avatarModels.count : 0}
                       detail={resources.avatarModels && resources.avatarModels.bytes > 0 ? formatSize(resources.avatarModels.bytes) : ''}
                       expandable={!!resources.avatarModels && resources.avatarModels.count > 0} />
          <ResourceRow adminId={adminId} email={email} kind="social" label="Réseaux sociaux connectés"
                       count={resources.socialNetworks ? resources.socialNetworks.total : unavailable}
                       detail={resources.socialNetworks ? `${resources.socialNetworks.active} actif(s)` : ''}
                       expandable={!!resources.socialNetworks && resources.socialNetworks.total > 0} />
        </tbody>
      </table>

      <p className={classNames(styles.note)}>
        Les appareils connectés n'ont pas de liste en base (ils ne vivent qu'en mémoire dans chaque chaîne) :
        seul leur compteur est indiqué. Les médias supprimés ne sont pas comptés.
      </p>

    </div>
  );
};

export default AdminResourcesTab;
